import { Box } from '@talos/kyoko';
import type { AgCartesianChartOptions } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { formatCostRiskBps, formatScenarioLabel } from '../../utils';
import type { ScenarioComparisonRow } from './utils';

export const DEFAULT_MARKER_COLOR = '#888888';

export interface ChartDataPoint {
  label: string;
  requestId: string;
  risk: number;
  cost: number;
  total: number;
}

export function buildScatterTooltip(datum: ChartDataPoint, colorMap: ReadonlyMap<string, string>) {
  const color = colorMap.get(datum.requestId) ?? DEFAULT_MARKER_COLOR;
  return {
    heading: datum.label,
    symbol: { marker: { fill: color, enabled: true } },
    data: [
      { label: 'Risk', value: `${formatCostRiskBps(datum.risk)} BPS` },
      { label: 'Cost', value: `${formatCostRiskBps(datum.cost)} BPS` },
      { label: 'Total', value: `${formatCostRiskBps(datum.total)} BPS` },
    ],
  };
}

const MIN_AXIS_PADDING = 0.05; // minimum 0.05 BPS visible range per side
const LABEL_PAD_RATIO = 0.15; // reserve ~15% of axis range for right-side labels

// When all data points are clustered tightly (or identical), AG Charts auto-scaling
// collapses the axis to a near-zero range, making dots overlap. This enforces a
// minimum visible spread so scattered points remain distinguishable.
function axisRange(
  values: number[],
  rightPad = 0,
  symmetricPad = 0
): { min?: number; max?: number } | Record<string, never> {
  if (values.length === 0) {
    return {}; // no data, let AG Charts handle defaults
  }

  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo;

  // If the spread is smaller than the minimum visible range, center the axis
  // around the midpoint and pad both sides so markers don't sit on top of each other.
  if (range < MIN_AXIS_PADDING * 2) {
    const mid = (lo + hi) / 2;
    return { min: mid - MIN_AXIS_PADDING, max: mid + MIN_AXIS_PADDING };
  }

  const hasPadding = symmetricPad > 0 || rightPad > 0;
  if (!hasPadding) {
    return {};
  }

  const symPad = range * symmetricPad;
  const extraRight = range * rightPad;
  return {
    min: lo - symPad,
    max: hi + symPad + extraRight,
  };
}

const ChartWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;

  > div {
    position: absolute;
    inset: 0;
  }
`;

export interface ScenarioChartProps {
  rows: ScenarioComparisonRow[];
  colorMap: ReadonlyMap<string, string>;
}

export function ScenarioChart({ rows, colorMap }: ScenarioChartProps) {
  const theme = useTheme();

  const chartOptions = useMemo((): AgCartesianChartOptions => {
    const dataPoints: ChartDataPoint[] = rows
      .filter(
        (row): row is ScenarioComparisonRow & { total: number; cost: number; risk: number } =>
          row.total != null && row.cost != null && row.risk != null
      )
      .map(row => ({
        label: formatScenarioLabel(row.version, row.name),
        requestId: row.requestId,
        risk: row.risk,
        cost: row.cost,
        total: row.total,
      }));

    const riskRange = axisRange(
      dataPoints.map(d => d.risk),
      LABEL_PAD_RATIO,
      0.1
    );
    const costRange = axisRange(
      dataPoints.map(d => d.cost),
      0,
      0.1
    );

    return {
      animation: { enabled: false },
      background: { fill: theme.backgroundCard },
      padding: { top: 20, right: 20, bottom: 10, left: 10 },
      data: dataPoints,
      series: [
        {
          type: 'scatter' as const,
          xKey: 'risk',
          yKey: 'cost',
          labelKey: 'label',
          size: 12,
          strokeWidth: 2,
          itemStyler: ({ datum }: { datum: ChartDataPoint }) => {
            const color = colorMap.get(datum.requestId) ?? DEFAULT_MARKER_COLOR;
            return { fill: color, stroke: color };
          },
          label: {
            enabled: true,
            placement: 'right',
            formatter: ({ datum }: { datum: ChartDataPoint }) =>
              `${datum.label}\nTotal: ${formatCostRiskBps(datum.total)} BPS`,
            color: theme.colorTextDefault,
            fontSize: 11,
            fontFamily: theme.fontFamily,
          },
          tooltip: {
            renderer: ({ datum }: { datum: ChartDataPoint }) => buildScatterTooltip(datum, colorMap),
          },
        },
      ],
      axes: {
        x: {
          type: 'number' as const,
          position: 'bottom' as const,
          ...riskRange,
          title: { text: 'Risk (BPS)', color: theme.colorTextSubtle, fontSize: 11 },
          label: {
            color: theme.colorTextSubtle,
            fontSize: 10,
            formatter: ({ value }: { value: number }) => formatCostRiskBps(value),
          },
          gridLine: { style: [{ stroke: theme.borderColorChartGridLine, lineDash: [4, 4] }] },
          line: { stroke: theme.borderColorChartAxis },
        },
        y: {
          type: 'number' as const,
          position: 'left' as const,
          ...costRange,
          title: { text: 'Cost (BPS)', color: theme.colorTextSubtle, fontSize: 11 },
          label: {
            color: theme.colorTextSubtle,
            fontSize: 10,
            formatter: ({ value }: { value: number }) => formatCostRiskBps(value),
          },
          gridLine: { style: [{ stroke: theme.borderColorChartGridLine, lineDash: [4, 4] }] },
          line: { stroke: theme.borderColorChartAxis },
        },
      },
      legend: { enabled: false },
    };
  }, [rows, colorMap, theme]);

  return (
    <ChartWrapper>
      <AgCharts options={chartOptions} />
    </ChartWrapper>
  );
}
